/**
 * Image picking, web implementation.
 *
 * A hidden `<input type="file">` is the only way to open a file chooser in a
 * browser; there is no React Native Web equivalent. The input is driven by a
 * labelled pressable so the control stays styled like the rest of the kit and
 * remains keyboard accessible.
 */

import { useRef, useState } from 'react';
import { Image, Pressable, View } from 'react-native';

import { Stack } from './layout';
import { Text } from './ui/text';
import { cn } from '../lib/utils';
import type { ImagePickerProps } from '../lib/image-types';

const MAX_BYTES = 5 * 1024 * 1024;

/**
 * Opens the platform file chooser and reports the chosen image.
 *
 * @param props Label, current image, and the pick handler.
 * @returns A drop-zone style control.
 */
export function ImagePicker({
  onPick,
  onCancel,
  currentUrl,
  label = 'Add a photo',
  disabled,
  aspectRatio = 4 / 3,
}: ImagePickerProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const shown = previewUrl ?? currentUrl ?? null;

  return (
    <Stack gap="sm">
      <Pressable
        onPress={() => {
          if (disabled) return;
          inputRef.current?.click();
        }}
        accessibilityRole="button"
        accessibilityLabel={label}
        className={cn(
          'border-input bg-muted items-center justify-center overflow-hidden rounded-xl border-2 border-dashed',
          disabled && 'opacity-60'
        )}
        // The preview ratio comes from a prop, which no class can express.
        style={{ aspectRatio }}>
        {shown ? (
          <Image
            source={{ uri: shown }}
            className="h-full w-full"
            resizeMode="cover"
            accessibilityIgnoresInvertColors
          />
        ) : (
          <Stack gap="xs" align="center" className="p-4">
            <Text className="text-subtle-foreground text-4xl leading-none">+</Text>
            <Text className="text-muted-foreground text-center text-xs font-medium">{label}</Text>
            <Text className="text-subtle-foreground text-center text-xs">
              JPEG, PNG, WebP, AVIF or HEIC · up to 5 MB
            </Text>
          </Stack>
        )}
      </Pressable>

      {shown ? (
        <Text className="text-subtle-foreground text-xs">
          Tap the image to choose a different one.
        </Text>
      ) : null}

      {error ? <Text className="text-destructive text-xs font-medium">{error}</Text> : null}

      {/* A raw DOM element: no React Native input can open a file chooser. */}
      <View className="h-0 overflow-hidden">
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/avif,image/heic"
          style={{ display: 'none' }}
          onChange={(event) => {
            const file = event.target.files?.[0];
            // Reset so choosing the same file twice still fires a change.
            event.target.value = '';

            if (!file) {
              onCancel?.();
              return;
            }
            if (file.size > MAX_BYTES) {
              setError('That image is larger than 5 MB. Please choose a smaller one.');
              return;
            }

            setError(null);
            setPreviewUrl(URL.createObjectURL(file));
            onPick({
              file,
              previewUrl: URL.createObjectURL(file),
              name: file.name,
              size: file.size,
            });
          }}
        />
      </View>
    </Stack>
  );
}
